import { useCallback, useEffect, useRef, useState } from 'react';
import type { LngLat } from '@yandex/ymaps3-types';
import { useDebouncedValue } from '@direct-frontend/stdlib';
import { useField, useFormInstance } from '@adv-frontend/form-kit';
import { useGeoCoderLocations } from '@/entities/geoCoder';
import { buildYMapLocation } from '@/shared/lib/yandexMap';
import { courierAddressForm } from '../../model/courierAddressForm';

export const useCourierAddressLocation = (formId: string) => {
    const { fields } = useFormInstance({ id: formId, form: courierAddressForm });
    const fullAddressField = useField(fields.fullAddress);

    const [location, setLocation] = useState(buildYMapLocation());

    const [loadGeoLocation, { data: locations = [] }] = useGeoCoderLocations();

    const debounceGeocode = useDebouncedValue(fullAddressField.value, 300);

    const [isLocalityLocationSet, setIsLocalityLocationSet] = useState<boolean>(false);
    const isFromMapDragRef = useRef<boolean>(false);

    useEffect(() => {
        if (debounceGeocode && !isFromMapDragRef.current) {
            loadGeoLocation({ geocode: debounceGeocode });
        }

        isFromMapDragRef.current = false;
    }, [loadGeoLocation, debounceGeocode]);

    useEffect(() => {
        // Не меняем локацию если геокод был вызван от драга карты
        if (!locations || isFromMapDragRef.current) {
            return;
        }

        const nearestHouse = locations.find((location) => location.metaDataProperty.GeocoderMetaData.kind === 'house');
        const nearestLocality = locations.find(
            (location) => location.metaDataProperty.GeocoderMetaData.kind === 'locality'
        );

        if (nearestHouse) {
            // eslint-disable-next-line react-hooks/set-state-in-effect -- Автоматический игнор в рамках DIRECT-288723
            setLocation(buildYMapLocation(nearestHouse));
            setIsLocalityLocationSet(true);

            return;
        }

        /**
         * Для центрирования карты при ее открытии по локали
         * в случаях когда адрес состоит только из локали
         */
        if (nearestLocality && !isLocalityLocationSet) {
            setLocation(buildYMapLocation(nearestLocality));
            setIsLocalityLocationSet(true);
        }
    }, [isLocalityLocationSet, locations, setLocation]);

    useEffect(() => {
        const address = locations[0]?.metaDataProperty.GeocoderMetaData.Address.formatted;
        const onlyCity = address?.split(',').length === 2; // Страна, город

        if (locations[0]) {
            fields.fullAddress.setValue(`${address}${onlyCity ? ', ' : ''}`);
        }
    }, [locations, fields.fullAddress]);

    const handleChangeLocation = useCallback(
        ({ geocode, isDrag = true }: { geocode: string | LngLat; isDrag?: boolean }) => {
            isFromMapDragRef.current = isDrag;
            loadGeoLocation({
                geocode,
                kind: 'house',
            });
        },
        [loadGeoLocation]
    );

    return {
        location,
        handleChangeLocation,
    };
};
